import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check, Smartphone, FolderLock, Sparkles, LayoutDashboard, CalendarDays, BellRing, GraduationCap } from "lucide-react";
import api from "../lib/api";
import { useExperience } from "../features/experience";
import { HUB_ICONS } from "./AdaptiveSidebar";

// Adaptive onboarding (Phase 5): four calm screens for brand-new users only.
// Signals map to hubs; the final screen previews the user's real navigation,
// including their chosen mobile tab. Skippable at every step; skipping keeps
// every hub on so nothing is ever hidden by accident.
export default function AdaptiveOnboarding({ hold = false }) {
  const navigate = useNavigate();
  const { experience, reload } = useExperience();
  const [step, setStep] = useState(0);
  const [chosen, setChosen] = useState(new Set());
  const [audience, setAudience] = useState(null);
  const [tab, setTab] = useState("vault");
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [lessonMap, setLessonMap] = useState({});

  const show = !hold && !dismissed && experience?.flags?.adaptive_onboarding
    && experience?.onboarding?.status === "not_started";

  React.useEffect(() => {
    if (!show) return;
    api.get("/academy/map").then((r) => setLessonMap(r.data.by_hub || {})).catch(() => {});
  }, [show]);
  const firstHub = [...chosen][0] || "core";
  const firstLesson = lessonMap[firstHub] || lessonMap.core || null;

  // Brand-new accounts (flag set at registration): hold a calm veil while the experience
  // payload loads so onboarding is the first thing they see, not a flash of dashboard.
  const isNewUser = typeof window !== "undefined" && localStorage.getItem("plannio_new_user") === "1";
  if (experience && isNewUser) localStorage.removeItem("plannio_new_user");
  if (!experience && isNewUser && !dismissed) {
    return (
      <div className="fixed inset-0 z-[70] flex items-center justify-center bg-[#FAFAF8]" data-testid="onboarding-veil">
        <div className="text-center">
          <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-[#0F766E] border-t-transparent" />
          <p className="mt-4 text-sm text-[#636E72]">Getting PlannioLife ready for you…</p>
        </div>
      </div>
    );
  }
  if (!show) return null;

  const life = (experience.layers?.life || []).filter((h) => h.status === "live");
  const selected = life.filter((h) => chosen.has(h.id));

  const toggle = (id) => setChosen((s) => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const save = (status, responses) => api.post("/experience/onboarding", { status, responses }).catch(() => {});

  const skip = async () => {
    setDismissed(true);
    await save("skipped", {});
    reload();
  };

  const finish = async () => {
    setBusy(true);
    await save("completed", {
      hubs: [...chosen],
      audience,
      mobile_tab: tab !== "vault" ? tab : null,
    });
    reload();
    setDismissed(true);
    navigate("/app/dashboard");
  };

  const next = () => {
    if (step === 0) save("in_progress", { hubs: [...chosen] });
    if (step === 1) save("in_progress", { audience });
    setStep((s) => s + 1);
  };

  const tabOptions = [
    { id: "vault", label: "Vault", icon: FolderLock },
    ...selected.map((h) => ({ id: h.id, label: h.label, icon: HUB_ICONS[h.icon] || Sparkles })),
  ];

  const CORE_PREVIEW = [
    { label: "Home", icon: LayoutDashboard },
    { label: "Calendar", icon: CalendarDays },
    { label: "Reminders", icon: BellRing },
    { label: "Vault", icon: FolderLock },
  ];

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-[#2D3436]/50 p-4 backdrop-blur-sm" data-testid="adaptive-onboarding" role="dialog" aria-modal="true" aria-label="Welcome to PlannioLife">
      <div className="relative flex max-h-[92vh] w-full max-w-2xl flex-col overflow-y-auto rounded-3xl bg-white p-8 shadow-2xl">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex gap-1.5">
            {[0, 1, 2, 3, 4].map((i) => (
              <span key={i} className={`h-1.5 rounded-full transition-all ${i === step ? "w-8 bg-[#0F766E]" : "w-4 bg-[#E2E8F0]"}`} />
            ))}
          </div>
          {step < 4 && <button data-testid="onboarding-skip" onClick={skip} className="text-sm font-medium text-[#9aa3a0] hover:text-[#636E72]">Skip for now</button>}
        </div>

        {step === 0 && (
          <div data-testid="onboarding-step-hubs">
            <h2 className="font-display text-2xl font-semibold text-[#2D3436]">Welcome to PlannioLife 👋</h2>
            <p className="mt-1 text-[#636E72]">Which parts of life would you like a hand with? Pick as many as you like, you can change this any time.</p>
            <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
              {life.map((h) => {
                const Icon = HUB_ICONS[h.icon] || Sparkles;
                const on = chosen.has(h.id);
                return (
                  <button key={h.id} data-testid={`onboarding-hub-${h.id}`} onClick={() => toggle(h.id)}
                    className={`relative rounded-2xl border p-4 text-left transition-all ${on ? "border-[#0F766E] bg-[#0F766E]/5" : "border-[#EBECE8] hover:border-[#cbd0cb]"}`}>
                    {on && <span className="absolute right-2.5 top-2.5 flex h-5 w-5 items-center justify-center rounded-full bg-[#0F766E] text-white"><Check className="h-3 w-3" strokeWidth={3} /></span>}
                    <Icon className={`h-6 w-6 ${on ? "text-[#0F766E]" : "text-[#636E72]"}`} strokeWidth={1.6} />
                    <div className="mt-2 font-medium text-[#2D3436]">{h.label}</div>
                    <div className="mt-0.5 text-xs leading-relaxed text-[#9aa3a0]">{h.tagline}</div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {step === 1 && (
          <div data-testid="onboarding-step-audience">
            <h2 className="font-display text-2xl font-semibold text-[#2D3436]">Who are you organising for?</h2>
            <p className="mt-1 text-[#636E72]">PlannioLife works beautifully alone, and even better together.</p>
            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              {[["me", "Just me", "A private space for the things you look after."],
                ["household", "My household", "Invite the people you share life with whenever you're ready."]].map(([k, label, desc]) => (
                <button key={k} data-testid={`onboarding-audience-${k}`} onClick={() => setAudience(k)}
                  className={`rounded-2xl border p-5 text-left transition-all ${audience === k ? "border-[#0F766E] bg-[#0F766E]/5" : "border-[#EBECE8] hover:border-[#cbd0cb]"}`}>
                  <div className="font-medium text-[#2D3436]">{label}</div>
                  <div className="mt-1 text-sm text-[#636E72]">{desc}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 2 && (
          <div data-testid="onboarding-step-tab">
            <h2 className="font-display text-2xl font-semibold text-[#2D3436]">Which hub earns your fourth tab?</h2>
            <p className="mt-1 text-[#636E72]">On your phone, Home, Calendar and Reminders are always one tap away. You choose the fourth. <Smartphone className="inline h-4 w-4 text-[#9aa3a0]" /></p>
            <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
              {tabOptions.map((o) => (
                <button key={o.id} data-testid={`onboarding-tab-${o.id}`} onClick={() => setTab(o.id)}
                  className={`rounded-2xl border p-4 text-center transition-all ${tab === o.id ? "border-[#0F766E] bg-[#0F766E]/5" : "border-[#EBECE8] hover:border-[#cbd0cb]"}`}>
                  <o.icon className={`mx-auto h-6 w-6 ${tab === o.id ? "text-[#0F766E]" : "text-[#636E72]"}`} strokeWidth={1.6} />
                  <div className="mt-2 text-sm font-medium text-[#2D3436]">{o.label}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 3 && (
          <div data-testid="onboarding-step-done">
            <h2 className="font-display text-2xl font-semibold text-[#2D3436]">PlannioLife is shaped around you</h2>
            <p className="mt-1 text-[#636E72]">Here is your navigation. Everything else stays a couple of taps away in the Hub Manager.</p>
            <div className="mt-5 rounded-2xl border border-[#EBECE8] bg-[#FAFAF8] p-5">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-[#9aa3a0]">Always there</div>
              <div className="mt-2 flex flex-wrap gap-2">
                {CORE_PREVIEW.map((c) => (
                  <span key={c.label} className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-xs font-medium text-[#2D3436] ring-1 ring-[#EBECE8]"><c.icon className="h-3.5 w-3.5 text-[#0F766E]" /> {c.label}</span>
                ))}
              </div>
              <div className="mt-4 text-[10px] font-semibold uppercase tracking-wide text-[#9aa3a0]">My hubs</div>
              <div className="mt-2 flex flex-wrap gap-2" data-testid="onboarding-preview-hubs">
                {(selected.length ? selected : life.filter((h) => h.id === "for_you")).map((h) => {
                  const Icon = HUB_ICONS[h.icon] || Sparkles;
                  return <span key={h.id} className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-xs font-medium text-[#2D3436] ring-1 ring-[#EBECE8]"><Icon className="h-3.5 w-3.5 text-[#B45309]" /> {h.label}</span>;
                })}
              </div>
              <div className="mt-4 flex items-center gap-2 text-xs text-[#636E72]">
                <Smartphone className="h-4 w-4 text-[#9aa3a0]" />
                Mobile tabs: Home · Calendar · Reminders · <span className="font-semibold text-[#0F766E]">{tabOptions.find((o) => o.id === tab)?.label || "Vault"}</span> · More
              </div>
            </div>
          </div>
        )}

        {step === 4 && (
          <div data-testid="onboarding-step-confirm" className="text-center">
            <img src="/planni-academy/wave.png" alt="" className="mx-auto h-36 w-auto rounded-2xl" />
            <h2 className="mt-4 font-display text-2xl font-semibold text-[#2D3436]">That's the setup done</h2>
            <p className="mx-auto mt-1 max-w-md text-[#636E72]">PlannioLife has been personalised around your life. You can change your hubs at any time from the Hub Manager.</p>
            {firstLesson && (
              <a href={`/community/academy/${firstLesson.slug}`} target="_blank" rel="noreferrer" data-testid="onboarding-first-lesson"
                className="mt-5 inline-flex items-center gap-2 rounded-xl border border-[#EBECE8] bg-[#FAFAF8] px-4 py-2.5 text-sm font-medium text-[#0F766E] transition-colors hover:border-[#0F766E]/40">
                <GraduationCap className="h-4 w-4" /> Start with a lesson: {firstLesson.title} · {firstLesson.reading_time}
              </a>
            )}
          </div>
        )}

        <div className="mt-8 flex items-center justify-between">
          {step > 0 ? (
            <button data-testid="onboarding-back" onClick={() => setStep((s) => s - 1)} className="inline-flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-medium text-[#636E72] hover:bg-[#f0f1ee]"><ArrowLeft className="h-4 w-4" /> Back</button>
          ) : <span />}
          {step < 4 ? (
            <button data-testid="onboarding-next" onClick={next}
              className="inline-flex items-center gap-2 rounded-xl bg-[#0F766E] px-6 py-2.5 text-sm font-medium text-white transition-transform hover:-translate-y-0.5">
              Continue <ArrowRight className="h-4 w-4" />
            </button>
          ) : (
            <button data-testid="onboarding-finish" onClick={finish} disabled={busy}
              className="inline-flex items-center gap-2 rounded-xl bg-[#0F766E] px-6 py-2.5 text-sm font-medium text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60">
              Take me Home <ArrowRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
